import React from "react";
import {
  NumberedSection,
  InfoBox,
  WarningBox,
  GreyCard,
  CheckItem,
} from "./UI";

export default function GuidelineSections() {
  return (
    <div className="container">
      {/* 01 */}
      <NumberedSection number="01" title="Purpose of These Guidelines">
        <p>
          Our platform connects both general users and business
          professionals. Every registered user starts as a member of the
          general community and may later create one or more business
          profiles.
        </p>
        <p className="fw-semibold mb-2">These guidelines explain:</p>
        <ul className="sz-check-list">
          <CheckItem>How users should behave and communicate</CheckItem>
          <CheckItem>How business and public profiles must be managed</CheckItem>
          <CheckItem>What activities are permitted or restricted</CheckItem>
          <CheckItem>How advertising and connections work</CheckItem>
          <CheckItem>
            What responsibilities users have regarding privacy and security
          </CheckItem>
        </ul>
        <p className="fw-semibold mb-0">
          We encourage all members to act ethically and contribute positively
          to the community.
        </p>
      </NumberedSection>

      {/* 02 */}
      <NumberedSection number="02" title="Respectful Behavior and Professional Conduct">
        <p>
          Every user must interact respectfully with others. Abusive,
          threatening, or discriminatory behavior is strictly forbidden.
        </p>
        <p>
          Users should avoid offensive or politically sensitive discussions,
          hate speech, vulgar language, or any content that could harm
          another's dignity.
        </p>
        <p>
          Harassment, personal attacks, or spreading false information about
          individuals or businesses is prohibited.
        </p>
        <p>
          Treat every connection and communication as a professional
          exchange, maintaining honesty, courtesy, and mutual respect.
        </p>
        <WarningBox>
          Violations of respectful conduct may result in content removal,
          suspension, or permanent account deactivation.
        </WarningBox>
      </NumberedSection>

      {/* 03 */}
      <NumberedSection number="03" title="Authenticity of Profiles and Information">
        <p className="fw-semibold mb-2">To maintain trust and integrity:</p>
        <ul className="sz-bullet-list">
          <li>All information provided during registration must be genuine and accurate.</li>
          <li>Users must not impersonate others or represent a business without authorization.</li>
          <li>
            A business profile must reflect the actual business entity and
            use its real name, logo, and registration details (if
            applicable).
          </li>
          <li>Using another company's name or identity without legal ownership is strictly prohibited.</li>
          <li>Users are solely responsible for the accuracy of their personal and business details.</li>
        </ul>
        <InfoBox bold>
          Providing false or misleading information may lead to verification
          requests, profile removal, or account suspension.
        </InfoBox>
      </NumberedSection>

      {/* 04 */}
      <NumberedSection number="04" title="User and Business Profiles">
        <p>There are two primary profile types on the platform:</p>
        <div className="row g-3">
          <div className="col-md-12">
            <GreyCard title="a. General Public Profile">
              <p>
                Upon signup, each user receives a general profile
                representing them as an individual member.
              </p>
              <p>
                Users can browse business listings, explore products, and
                connect with other members as permitted.
              </p>
              <p className="mb-0">
                General users can later convert their status to a business
                owner by creating a business profile.
              </p>
            </GreyCard>
          </div>
          <div className="col-md-12">
            <GreyCard title="b. Business Profile">
              <p>
                A user may open a business or company profile by submitting
                required business information.
              </p>
              <p>
                Each business profile is distinct and represents a specific
                shop or company (e.g., Retailer, Wholesaler, Jeweller,
                Industrialist, Transporter, Property Dealer, Food Supplier,
                Scrap Dealer, etc.).
              </p>
              <p>
                Business owners can manage shop details, upload products,
                edit business information, and communicate with buyers and
                partners.
              </p>
              <p className="mb-0">
                Multiple shops can be managed under one account, provided each
                follows our platform's policies.
              </p>
            </GreyCard>
          </div>
        </div>
      </NumberedSection>

      {/* 05 */}
      <NumberedSection number="05" title="Buying and Selling Environment">
        <p className="fw-semibold">
          Our platform provides a connection based trading environment, not a
          direct e-commerce system.
        </p>
        <div className="row g-3">
          <div className="col-md-6">
            <GreyCard title="a. Selling Side">
              <p>
                Business profiles can display their products, services,
                contact details, and shop information.
              </p>
              <p>
                Sellers may update their listings, add product photos, and
                maintain accurate pricing and descriptions.
              </p>
              <p className="mb-0">
                Sellers are responsible for ensuring that all listed products
                and services are legitimate, safe, and compliant with
                applicable laws.
              </p>
            </GreyCard>
          </div>
          <div className="col-md-6">
            <GreyCard title="b. Buying Side">
              <p>
                Buyers can explore available shops, check product details,
                prices, and locations, and contact sellers directly.
              </p>
              <p>
                The platform does not handle any financial transactions,
                deliveries, or order management.
              </p>
              <p>
                Any purchase, negotiation, or agreement occurs outside the
                platform and entirely at the users' own risk and
                responsibility.
              </p>
              <p className="mb-0">
                We do not guarantee the authenticity, quality, or legality of
                any product or service listed by users.
              </p>
            </GreyCard>
          </div>
        </div>
      </NumberedSection>

      {/* 06 */}
      <NumberedSection number="06" title="Connection and Networking Rules">
        <p>
          Our connection system allows professionals and businesses to build
          meaningful links within defined boundaries:
        </p>
        <ul className="sz-bullet-list">
          <li>Connections can be established only with relevant business types according to your own category.</li>
        </ul>
        <InfoBox>
          <strong>Example: </strong>A Retailer can connect with Wholesalers
          and General Public users, but not directly with Industrialists
          unless authorized.
        </InfoBox>
        <ul className="sz-bullet-list">
          <li>
            Users can communicate through our Messenger system and share
            updates through Timelines visible to connected profiles.
          </li>
          <li>
            Unsolicited connection requests, spam messaging, or sending
            repeated unwanted communications are not allowed.
          </li>
          <li>
            To expand visibility or reach beyond the allowed business types,
            users can upgrade to Basic, Moderate, or Premium Plans, each
            offering broader connection access.
          </li>
          <li>
            The platform reserves the right to review and approve connections
            to maintain network relevance and safety.
          </li>
        </ul>
      </NumberedSection>

      {/* 07 */}
      <NumberedSection number="07" title="Timeline Sharing and Advertisement (Ads)">
        <p>
          The Timeline allows users and businesses to share posts, updates,
          and announcements with their connections.
        </p>
        <div className="row g-3">
          <div className="col-md-12">
            <GreyCard title="a. Timeline Posts">
              <ul className="sz-bullet-list mb-0">
                <li>Timeline posts should be professional and relevant to business or community engagement.</li>
                <li>Posts must not contain false claims, explicit material, or offensive imagery.</li>
                <li>Users may only share content with audiences permitted under their business type and plan.</li>
              </ul>
            </GreyCard>
          </div>
          <div className="col-md-12">
            <GreyCard title="b. Advertisements">
              <p>
                Businesses wishing to reach categories outside their direct
                network may use our Ads Section.
              </p>
              <p className="fw-semibold mb-1">To place an ad, users must define:</p>
              <ul className="sz-bullet-list">
                <li>The target business type or audience</li>
                <li>Duration and daily budget</li>
                <li>Ad content (text, images, or video)</li>
              </ul>
              <p>Ads are reviewed for compliance before publication.</p>
              <p>
                We may reject or remove ads that contain prohibited material,
                false information, or illegal promotions.
              </p>
              <p>Approved ads will run as per the selected budget and time frame.</p>
              <p className="mb-0">
                The user receives an invoice upon successful purchase of any
                advertising plan.
              </p>
            </GreyCard>
          </div>
        </div>
      </NumberedSection>

      {/* 08 */}
      <NumberedSection number="08" title="Prohibited Content and Activities">
        <p className="fw-semibold">
          To protect community integrity, the following actions are strictly
          prohibited:
        </p>
        <ul className="sz-bullet-list mb-0">
          <li>Uploading or sharing illegal, violent, fraudulent, or adult content.</li>
          <li>Using automated bots or fake accounts to manipulate data or visibility.</li>
          <li>Engaging in scams, phishing, or deceptive business practices.</li>
          <li>Posting misleading information about products, services, or pricing.</li>
          <li>Violating copyright, trademark, or intellectual property rights.</li>
          <li>Attempting to hack, damage, or bypass platform systems or security measures.</li>
          <li>Promoting gambling, drugs, or any form of restricted trade.</li>
        </ul>
        <WarningBox>
          Any violation may lead to content deletion, account restriction,
          legal action, or permanent ban.
        </WarningBox>
      </NumberedSection>

      {/* 09 */}
      <NumberedSection number="09" title="Privacy, Security, and Confidentiality">
        <p>
          We provide strong authentication and password protection systems to
          secure user accounts.
        </p>
        <p>Users are responsible for keeping their login credentials private.</p>
        <p>
          Sharing your account or giving access to unauthorized persons is
          not allowed.
        </p>
        <p>
          Do not post or share private details of other users, including
          phone numbers, addresses, or financial data.
        </p>
        <p>
          We respect your privacy and handle personal and business data in
          accordance with our Privacy Policy.
        </p>
        <InfoBox bold>
          The platform will never request passwords or confidential
          information through unofficial channels.
        </InfoBox>
      </NumberedSection>

      {/* 10 */}
      <NumberedSection number="10" title="Reporting Misuse and Violations">
        <p>
          Users can report inappropriate content, spam, or rule violations
          directly from the platform.
        </p>
        <p>
          Our moderation team reviews all reports carefully and takes
          appropriate action.
        </p>
        <p>
          Actions may include warning notifications, temporary suspension, or
          permanent removal of accounts or content.
        </p>
        <p className="mb-0">
          Users found repeatedly violating guidelines may lose access to
          premium features or future participation rights.
        </p>
      </NumberedSection>

      {/* 11 */}
      <NumberedSection number="11" title="Advertisement, Plans, and Payment Responsibility">
        <p>
          Paid services (such as upgraded plans or ads) are non-transferable
          and must be purchased through official channels only.
        </p>
        <p>Once payment is processed, an invoice will be issued for record purposes.</p>
        <p>
          Misuse of paid features, including unauthorized promotion or
          impersonation, may result in suspension without refund.
        </p>
        <p className="mb-0">
          The platform reserves the right to modify or discontinue pricing
          plans at any time.
        </p>
      </NumberedSection>

      {/* 12 */}
      <NumberedSection number="12" title="Legal and Transaction Disclaimer">
        <p className="fw-semibold">
          The platform serves only as a connection bridge between users and
          businesses.
        </p>
        <p>
          We are not responsible for any product, service, deal, transaction,
          or dispute that occurs between users.
        </p>
        <p>
          Any communication or agreement made between two parties is entirely
          independent of the platform.
        </p>
        <p className="mb-0">
          In case of disputes, users must resolve the issue directly; we may
          assist by providing information if legally required.
        </p>
      </NumberedSection>

      {/* 13 */}
      <NumberedSection number="13" title="Enforcement and Compliance">
        <p>
          We reserve the right to suspend, modify, or terminate accounts that
          violate these guidelines or other legal requirements.
        </p>
        <p>
          Users may lose access to content, connections, or advertisements if
          found misusing the system.
        </p>
        <p className="mb-0">
          Severe or repeated offenses may lead to permanent removal and
          reporting to authorities.
        </p>
      </NumberedSection>

      {/* 14 */}
      <NumberedSection number="14" title="Updates to These Guidelines">
        <p>
          These guidelines may be updated periodically to reflect system
          improvements or changes in laws.
        </p>
        <p>
          When updates occur, users will be notified or can review the new
          version on our platform.
        </p>
        <p className="mb-0">
          Continued use of our services after updates implies acceptance of
          the revised guidelines.
        </p>
      </NumberedSection>

      {/* 15 */}
      <section className="sz-numbered-section pb-3">
        <div className="d-flex align-items-baseline gap-2 mb-3 sz-section-heading">
          <span className="sz-section-number">15.</span>
          <h2 className="sz-section-title mb-0">Our Commitment</h2>
        </div>
        <InfoBox>
          <p>
            We aim to maintain a secure and cooperative community where
            individuals and businesses can grow together.
          </p>
          <p className="mb-0">
            Following these User Guidelines ensures that all members, from
            general users to professional businesses, can benefit from a
            reliable, transparent, and respectful digital ecosystem.
          </p>
        </InfoBox>
      </section>
    </div>
  );
}
